/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useEffect, useState } from 'react';
import { useParams, NavLink } from 'react-router-dom';
import YouTube from 'react-youtube';
import Swal from 'sweetalert2';
import Iframe from 'react-iframe';

// @mui material components
import Card from '@mui/material/Card';
import CardHeader from '@mui/material/CardHeader';
import CardContent from '@mui/material/CardContent';
import CardActions from '@mui/material/CardActions';
import Icon from '@mui/material/Icon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import Grid from '@mui/material/Grid';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import IconButton from '@mui/material/IconButton';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import InputAdornment from '@mui/material/InputAdornment';
import TableHead from '@mui/material/TableHead';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Tooltip from '@mui/material/Tooltip';
import Skeleton from '@mui/material/Skeleton';
import FormControlLabel from '@mui/material/FormControlLabel';
import Switch from '@mui/material/Switch';

// Material Dashboard 2 PRO React components
import MDBox from 'components/MDBox';
import MDButton from 'components/MDButton';
import MDInput from 'components/MDInput';
import MDCheckbox from 'components/MDCheckbox';
import MDSelect from 'components/MDSelect';
import MDTypography from 'components/MDTypography';
import TabPanel from 'components/TabPanel/TabPanel';
import MDEditor from 'components/MDEditor';
import MDSelectAuto from 'components/MDSelectAuto';
import MDSelectGroup from 'components/MDSelectGroup';

// Material Dashboard 2 PRO React examples
import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import Navbar from 'examples/Navbars/NavBar';
import Footer from 'examples/Footer';
import { useTranslation } from 'react-i18next';
import { APP_STATE } from 'services/Define';
import { FetchData } from 'services/Request';
import { useSelector } from 'react-redux';
import * as Func from 'services/Function';
import { TextField } from '@mui/material';
import { numericInputHandlers } from 'utils';
import step from 'assets/theme/components/stepper/step';
import ProductUpload from './ProductUpload';
import ProductBarcode from './ProductBarcode';
import ProductImage from './ProductImage';
import ProductSupplier from './ProductSupplier';
import ProductBranch from './ProductBranch';
import ProductAlert from './ProductAlert';
import ProductBrand from './ProductBrand';
import ProductCost from './ProductCost';
import ProductGroup from './ProductGroup';

const initAttr = {
  id: '',
  sku: '',
  barcode2: '',
  dep_id: '',
  cat_id: '',
  group_id: '',
  subgroup_id: '',
  product_text: '',
  image_id: '',
  unit_id: '',
  unitadd_id: '',
  unitbuy_id: '',
  brand_id: '',
  color_id: '',
  barcode: '',
  vat_id: '2',
  image: '',
  name: '',
  name_en: '',
  detail: '',
  detail_en: '',
  content: '',
  content_en: '',
  drug_type_9: 'N',
  drug_type_10: 'N',
  drug_type_11: 'N',
  sell_online: 'N',
  autoid: 'Y',
  fg: 'Y',
  rm: 'N',
  autoprefix: 'RT',
  cost: '',
  cost_fix: '',
  cost_sell: '',
  cost_markup: '',
  price: '',
  youtube: '',
  min_stock: '0',
  max_stock: '999999',
  check_stock: 'N',
  active_buy: 'Y',
  change_unit: 'Y',
  enable: 'Y',
  pharmacy: 'N',
  medicine: 'N',
  created_at: '',
  updated_at: '',
  display_name: '',
  notification_no: '',
  shelf_life: 0,
  manufacturer: '',
  width: 0,
  height: 0,
  depth: 0,
  gross_weight: 0,
  net_weight: 0,
  volume: 0,
};

const initNavigate = {
  first: '',
  prev: '',
  next: '',
  last: '',
};

const initFind = {
  search: '',
  order_by: 'tb.id',
  order_sort: 'desc',
  nopic: 'N',
  noprice: 'N',
  noactive: 'N',
  grade: 'ALL',
  page: 1,
  perPage: 12,
  pageTotal: 0,
};

function Product() {
  const mod = 'product';
  const app = useSelector((state) => state[APP_STATE]);
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const [pageTotal, setPageTotal] = useState(0);
  const [pageNo, setPageNo] = useState(1);
  const [record, setRecord] = useState([]);
  const [show, setShow] = useState(null);
  const [attr, setAttr] = useState(initAttr);
  const [find, setFind] = useState(initFind);
  const [navigate, setNavigate] = useState(initNavigate);
  const [notify, setNotify] = useState(null);
  const [confirm, setConfirm] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadView, setLoadView] = useState(true);
  const [modalAdd, setModalAdd] = useState(false);
  const [cboDep, setCboDep] = useState([]);
  const [cboCat, setCboCat] = useState([]);
  const [cboGrop, setCboGroup] = useState([]);
  const [cboSubGroup, setCboSubGroup] = useState([]);
  const [cboBrand, setCboBrand] = useState([]);
  const [cboUnit, setCboUnit] = useState([]);
  const [image, setImage] = useState([]);
  const [tab, setTab] = useState(0);
  const [cboVatType, setCboVatType] = useState([]);
  const [content, setContent] = useState('');
  const [contentEN, setContentEN] = useState('');
  const [addText, setAddText] = useState(false);
  const [textInfo, setTextInfo] = useState([]);
  const [textError, setTextError] = useState([]);
  const [excelExample, setExcelExample] = useState('');
  const [modalPhoto, setModalPhoto] = useState(false);
  const [modalPrint, setModalPrint] = useState(false);
  const [modalView, setModalView] = useState(false);
  const [photo, setPhoto] = useState({});
  const [qrcode, setQrCode] = useState('');
  const [printUrl, setPrintUrl] = useState('');
  const [editPrice, setEditPrice] = useState(false);
  const [alertProduct, setAlertProduct] = useState(false);
  const [brand, setBrand] = useState({ id: '', name: '' });
  const [unit, setUnit] = useState({ id: '', name: '' });
  const [dep, setDep] = useState({ id: '', name: '' });
  const [cat, setCat] = useState({ id: '', name: '' });
  const [group, setGroup] = useState({ id: '', name: '' });
  const [subgroup, setSubGroup] = useState({ id: '', name: '' });
  const [cboGrade, setCboGrade] = useState([]);
  const [filterCat, setFilterCat] = useState([]);
  const [filterGroup, setFilterGroup] = useState([]);
  const [filterSubGroup, setFilterSubGroup] = useState([]);
  const [urlExcel, setUrlExcel] = useState('');
  const [excelLoading, setExcelLoading] = useState(false);

  const handlers = numericInputHandlers(attr, setAttr);

  const openShow = (e) => setShow(e.currentTarget);
  const closeShow = () => setShow(null);

  const showNotify = (msg, color) => {
    setNotify(
      <Snackbar
        open
        autoHideDuration={3000}
        onClose={() => setNotify(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setNotify(null)} severity={color} variant='filled'>
          {msg}
        </Alert>
      </Snackbar>
    );
  };

  function isOverLimit(textContent, maxLength = 100) {
    const chars = [...textContent];
    let count = 1;

    chars.forEach((char) => {
      if (!char.match(/\p{M}/u)) {
        count += 1;
      }
    });
    if (count > maxLength) {
      return true;
    }
    return false;
  }

  const view = (data) => {
    setFind(data);
    setLoadView(true);
    closeShow();

    FetchData('POST', `/${mod}`, app.token, {
      lang: app.lang,
      search: data.search,
      page: data.page,
      per_page: data.perPage,
      orderby: data.order_by,
      ordersort: data.order_sort,
      nopic: data.nopic,
      noprice: data.noprice,
      noactive: data.noactive,
      grade: data.grade,
    }).then((result) => {
      if (result.status === 'ok') {
        setRecord(result.data);
        setUrlExcel(result.excel);

        setPageTotal(result.total_pages);
        setPageNo(result.page);
      }
      setLoadView(false);
    });
  };

  const excel = () => {
    setExcelLoading(true);

    const params = new URLSearchParams();
    params.append('search', find.search);
    params.append('grade', find.grade);
    const url = `${urlExcel}/download?${params.toString()}`;
    const iframe = document.createElement('iframe');
    iframe.style.display = 'none';
    iframe.src = url;

    iframe.onload = () => {
      setExcelLoading(false);
      document.body.removeChild(iframe);
    };

    iframe.onerror = () => {
      setExcelLoading(false);
      document.body.removeChild(iframe);
    };

    document.body.appendChild(iframe);

    setTimeout(() => {
      setExcelLoading(false);
    }, 10000);
  };

  const reload = () => {
    view(find);
  };

  const open = () => {
    setLoading(true);
    setConfirm(false);
    setAttr(initAttr);
    setTab(0);

    FetchData('GET', `/${mod}/${id}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setNavigate(result.navigate);
        setAttr(result.data);
        setContent(result.data.content);
        setContentEN(result.data.content_en);
        setImage(result.image);
        setBrand({ id: result.data.brand_id, name: result.data.brand_name });
        setUnit({ id: result.data.unit_id, name: result.data.unit_name });
        setDep({ id: result.data.dep_id, name: result.data.dep_name });
        setCat({ id: result.data.cat_id, name: result.data.cat_name });
        setGroup({ id: result.data.group_id, name: result.data.group_name });
        setSubGroup({ id: result.data.subgroup_id, name: result.data.subgroup_name });
        setFilterCat(result.cat);
        setFilterGroup(result.group);
        setFilterSubGroup(result.subgroup);
        setLoading(false);
      }
    });
  };

  const addConfirm = () => {
    if (addText) {
      if (!Func.verifyLength(attr.product_text, 1)) return;
    } else {
      if (attr.autoid === 'N' && !Func.verifyLength(attr.sku, 1)) return;
      if (
        (attr.autoid === 'N' && Func.verifyLength(attr.sku, 1)) ||
        !['RT', 'SV', 'TS', 'PM'].some((p) => (attr.sku || '').toUpperCase().startsWith(p))
      ) {
        showNotify('รหัสสินค้า (SKU) ต้องขึ้นต้นด้วย RT, SV, TS หรือ PM เท่านั้น', 'error');
        return;
      }
      if (!Func.verifyLength(attr.name, 1)) return;
      if (!Func.verifyLength(attr.unit_id, 1)) return;
      if (!Func.verifyLength(attr.vat_id, 1)) return;
    }

    setConfirm(true);
  };

  const addInfo = () => {
    setLoading(true);
    setModalAdd(false);
    setConfirm(false);

    FetchData('PUT', `/${mod}`, app.token, {
      brand_id: brand.id,
      unit_id: unit.id,
      add_text: 'N',
      group_id: attr.dep_id,
      cat_id: attr.cat_id,
      subcat_id: attr.group_id,
      division_id: attr.subgroup_id,
      unitadd_id: attr.unitadd_id,
      unitbuy_id: attr.unitbuy_id,
      color_id: attr.color_id,
      sku: attr.sku,
      barcode: attr.barcode,
      barcode2: attr.barcode2,
      fg: attr.fg,
      rm: attr.rm,
      vat_id: attr.vat_id,
      name: attr.name,
      name_en: attr.name_en,
      detail: attr.detail,
      detail_en: attr.detail_en,
      cost: attr.cost,
      cost_fix: attr.cost_fix,
      price: attr.price,
      min_stock: attr.min_stock,
      max_stock: attr.max_stock,
      check_stock: attr.check_stock,
      enable: attr.enable,
      active_buy: attr.active_buy,
      drug_type_9: attr.drug_type_9,
      drug_type_10: attr.drug_type_10,
      drug_type_11: attr.drug_type_11,
      sell_online: attr.sell_online,
      autoid: attr.autoid,
      autoprefix: attr.autoprefix.toUpperCase(),
      display_name: attr.display_name,
      notification_no: attr.notification_no,
      shelf_life: attr.shelf_life,
      manufacturer: attr.manufacturer,
      width: attr.width,
      height: attr.height,
      depth: attr.depth,
      gross_weight: attr.gross_weight,
      net_weight: attr.net_weight,
      volume: attr.volume,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        window.location.replace(`${mod}/${result.id}`);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        setModalAdd(true);
        showNotify(result.msg, 'error');
      }
    });
  };

  const addInfoText = () => {
    setLoading(true);
    setConfirm(false);
    setTextError([]);
    setTextInfo([]);

    FetchData('PUT', `/${mod}`, app.token, {
      add_text: 'Y',
      product_text: attr.product_text,
    }).then((result) => {
      setLoading(false);
      setModalAdd(true);

      if (result.status === 'ok') {
        view(find);
        setTextError(result.error);
        setTextInfo(result.info);
        showNotify(t(`alert.addcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const add = () => {
    if (addText) addInfoText();
    else addInfo();
  };

  const editConfirm = () => {
    if (!Func.verifyLength(attr.dep_id, 1)) return false;
    if (!Func.verifyLength(attr.cat_id, 1)) return false;
    if (!Func.verifyLength(attr.group_id, 1)) return false;
    if (!Func.verifyLength(attr.subgroup_id, 1)) return false;
    if (!Func.verifyLength(attr.sku, 1)) return false;
    if (!['RT', 'SV', 'TS', 'PM'].some((p) => (attr.sku || '').toUpperCase().startsWith(p))) {
      showNotify('รหัสสินค้า (SKU) ต้องขึ้นต้นด้วย RT, SV, TS หรือ PM เท่านั้น', 'error');
      return false;
    }
    if (!Func.verifyLength(attr.name, 1)) return false;
    if (!Func.verifyLength(attr.unit_id, 1)) return false;
    if (!Func.verifyLength(attr.vat_id, 1)) return false;

    return true;
  };

  const edit = () => {
    if (!editConfirm()) return;

    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);
        setConfirm(false);

        if (
          cboCat.filter((item) => item.dep_id === attr.dep_id && item.id === attr.cat_id).length ===
          0
        ) {
          setLoading(false);
          showNotify('ข้อมูล Category ไม่ถูกต้อง', 'error');
          return;
        }

        if (
          cboGrop.filter((item) => item.cat_id === attr.cat_id && item.id === attr.group_id)
            .length === 0
        ) {
          setLoading(false);
          showNotify('ข้อมูล Group ไม่ถูกต้อง', 'error');
          return;
        }

        if (
          cboSubGroup.filter(
            (item) => item.group_id === attr.group_id && item.id === attr.subgroup_id
          ).length === 0
        ) {
          setLoading(false);
          showNotify('ข้อมูล Subgroup ไม่ถูกต้อง', 'error');
          return;
        }

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          brand_id: brand.id,
          unit_id: unit.id,
          group_id: attr.dep_id,
          cat_id: attr.cat_id,
          subcat_id: attr.group_id,
          division_id: attr.subgroup_id,
          unitadd_id: attr.unitadd_id,
          unitbuy_id: attr.unitbuy_id,
          color_id: attr.color_id,
          vat_id: attr.vat_id,
          name: attr.name,
          name_en: attr.name_en,
          detail: attr.detail,
          detail_en: attr.detail_en,
          enable: attr.enable,
          active_buy: attr.active_buy,
          pharmacy: attr.pharmacy,
          medicine: attr.medicine,
          drug_type_9: attr.drug_type_9,
          drug_type_10: attr.drug_type_10,
          drug_type_11: attr.drug_type_11,
          sell_online: attr.sell_online,
          fg: attr.fg,
          rm: attr.rm,
          display_name: attr.display_name,
          notification_no: attr.notification_no,
          shelf_life: attr.shelf_life,
          manufacturer: attr.manufacturer,
          width: attr.width,
          height: attr.height,
          depth: attr.depth,
          gross_weight: attr.gross_weight,
          net_weight: attr.net_weight,
          volume: attr.volume,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            // open();
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setConfirm(false);
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const print = (barcode) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/print`, app.token, {
      lang: app.lang,
      barcode: barcode,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setPrintUrl(result.print);
        setQrCode(barcode);
        setModalPrint(true);
      }
    });
  };

  const qr = (item) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/qrcode`, app.token, {
      lang: app.lang,
      id: item.product_id,
    }).then((result) => {
      setLoading(false);
      if (result.status === 'ok') {
        setPhoto({
          image: result.qrcode,
          name: item.code,
        });
        setModalPhoto(true);
      }
    });
  };

  const checkCode = (prefix) => {
    FetchData('OPTIONS', `/${mod}/checkcode`, app.token, {
      lang: app.lang,
      prefix: prefix,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr({ ...attr, sku: result.code });
      }
    });
  };

  const editYoutube = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          youtube: attr.youtube,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setConfirm(false);
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const editContent = () => {
    Swal.fire({
      title: t(`alert.confirm_edit_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('PATCH', `/${mod}/${attr.id}`, app.token, {
          content: content,
          content_en: contentEN,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            setConfirm(false);
            showNotify(t(`alert.${result.msg}`), 'error');
          }
        });
      }
    });
  };

  const upload = (filedata) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/upload`, app.token, {
      id: id,
      filedata: filedata,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setImage(result.image);
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const merge = (item) => {
    Swal.fire({
      title: 'SKU ปลายทางที่ต้องการผสาน',
      input: 'text',
      inputAttributes: {
        autocapitalize: 'off',
      },
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.save`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((result) => {
      if (result.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/merge`, app.token, {
          id: item.id,
          sku: Swal.getInput()?.value || '',
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            window.location.replace(`${mod}/${result.id}`);
            showNotify(t(`alert.editcomplete`), 'success');
          } else {
            showNotify(t(`alert.saveerror`), 'error');
          }
        });
      }
    });
  };

  const del = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('DELETE', `/${mod}/${item.product_id}`, app.token, {}).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            view(find);
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const delImage = (item) => {
    Swal.fire({
      title: t(`alert.confirm_delete_title`),
      icon: 'warning',
      showDenyButton: true,
      confirmButtonColor: '#3085d6',
      confirmButtonText: t(`element.deleteconfirm`),
      denyButtonText: t(`element.cancel`),
      denyButtonColor: '#dc3545',
      reverseButtons: true,
    }).then((swal) => {
      if (swal.isConfirmed) {
        setLoading(true);

        FetchData('OPTIONS', `/${mod}/delimage`, app.token, {
          id: item.id,
          product_id: item.product_id,
        }).then((result) => {
          setLoading(false);

          if (result.status === 'ok') {
            setImage(result.image);
            showNotify(t(`alert.deletecomplete`), 'success');
          } else {
            showNotify(t(`alert.deleteerror`), 'error');
          }
        });
      }
    });
  };

  const pin = (item) => {
    setLoading(true);

    FetchData('OPTIONS', `/${mod}/pin`, app.token, {
      id: item.id,
      image_id: item.image_id,
    }).then((result) => {
      setLoading(false);

      if (result.status === 'ok') {
        setAttr({ ...attr, image: result.image.image });
        showNotify(t(`alert.editcomplete`), 'success');
      } else {
        showNotify(t(`alert.saveerror`), 'error');
      }
    });
  };

  const fgDisplay = (item) => {
    if (item.fg === 'Y' && item.rm === 'Y') {
      return (
        <span>
          {Func.codeStatus('สินค้า', 'info')} {Func.codeStatus('วัตถุดิบ', 'danger')}
        </span>
      );
    }

    if (item.fg === 'Y') return Func.codeStatus('สินค้า', 'info');
    if (item.rm === 'Y') return Func.codeStatus('วัตถุดิบ', 'danger');

    return 'N/A';
  };

  const renderLoading = () => {
    return (
      <Backdrop sx={{ color: '#fff', zIndex: 9999 }} open={loading}>
        <CircularProgress color='inherit' />
      </Backdrop>
    );
  };

  const renderShow = (
    <Menu
      anchorEl={show}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      open={Boolean(show)}
      onClose={closeShow}
      keepMounted
    >
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 12 })}>
        {t(`element.show_record`, { num: 12 })}
      </MenuItem>
      <MenuItem onClick={() => view({ ...find, page: 1, perPage: 24 })}>
        {t(`element.show_record`, { num: 24 })}
      </MenuItem>
    </Menu>
  );

  const init = () => {
    setLoadView(true);
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setAttr(initAttr);
        setCboGrade(result.grade);
        setCboDep(result.dep);
        setCboCat(result.cat);
        setCboGroup(result.group);
        setCboSubGroup(result.subgroup);
        setCboBrand(result.brand);
        setCboUnit(result.unit);
        setCboVatType(result.vattype);
        setExcelExample(result.excel_example);
        setAlertProduct(result.alert * 1 > 0);
        setFilterCat([]);
        setFilterGroup([]);
        setFilterSubGroup([]);

        if (id === undefined) {
          view(find);
        } else {
          open();
        }
      } else {
        Func.exitApp();
      }
    });
  };

  const refresh = () => {
    FetchData('GET', `/${mod}`, app.token, {
      lang: app.lang,
    }).then((result) => {
      if (result.status === 'ok') {
        setCboDep(result.dep);
        setCboCat(result.cat);
        setCboGroup(result.group);
        setCboSubGroup(result.subgroup);
        setCboBrand(result.brand);
        setCboUnit(result.unit);
        setCboVatType(result.vattype);
      }
    });
  };

  const filterCategory = (v) => {
    setDep(v);
    setFilterCat(cboCat.filter((item) => item.dep_id === v.id));
  };

  const filterGroupFunc = (v) => {
    setCat(v);
    setFilterGroup(cboGrop.filter((item) => item.cat_id === v.id));
  };

  const filterSubGroupFunc = (v) => {
    setGroup(v);
    setFilterSubGroup(cboSubGroup.filter((item) => item.group_id === v.id));
  };

  const btnOpen = (item) => {
    if (item.product_group === 'Y') {
      return (
        <NavLink to={`/${mod}/${item.product_id}`}>
          <Tooltip title={`สินค้ากลุ่ม #${item.id}`}>
            <MDButton
              variant='text'
              color='error'
              sx={{
                p: 0,
                m: 0,
                fontSize: '1rem',
                display: 'block',
                textAlign: 'left',
                minHeight: 'unset',
              }}
              onClick={() => setModalView(false)}
            >
              {item.code} &nbsp; <i className='fad fa-shopping-bag' />
            </MDButton>
          </Tooltip>
        </NavLink>
      );
    }

    return (
      <NavLink to={`/${mod}/${item.product_id}`}>
        <Tooltip title={`สินค้าเดี่ยว #${item.id}`}>
          <MDButton
            variant='text'
            color='info'
            sx={{
              p: 0,
              m: 0,
              fontSize: '1rem',
              display: 'block',
              textAlign: 'left',
              minHeight: 'unset',
            }}
            onClick={() => setModalView(false)}
          >
            {item.code}
          </MDButton>
        </Tooltip>
      </NavLink>
    );
  };

  const btnImage = (item) => {
    return (
      <Tooltip title='ดูรูปภาพ'>
        <MDButton
          variant='text'
          color='info'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
          }}
          onClick={() => {
            if (!modalView) {
              setPhoto({
                image: item.image,
                name: item.code,
              });
              setModalPhoto(true);
            }
          }}
        >
          {Func.imageDisplay(item)}
        </MDButton>
      </Tooltip>
    );
  };

  const btnDel = (item) => {
    if (!app.perm.del) return null;
    if (item.enable === 'Y') return null;

    return (
      <Tooltip title={t(`element.delete`)}>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => del(item)}>
          <Icon>delete</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnQuantity = (item) => {
    return (
      <Tooltip title='ปริมาณ'>
        <MDButton
          variant='text'
          color='secondary'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'left',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
        >
          <Icon>inventory_2</Icon> &nbsp;
          {Func.numberDisplay(item.quantity_balance)} {item.unit_name}
        </MDButton>
      </Tooltip>
    );
  };

  const btnGrade = (item) => {
    if (item.best_name === '') {
      return (
        <Tooltip title='Grade'>
          <MDButton
            variant='text'
            color='secondary'
            size='small'
            iconOnly
            sx={{ marginLeft: 'auto' }}
          >
            N/A
          </MDButton>
        </Tooltip>
      );
    }

    return (
      <Tooltip title={`Grade ${item.best_name}`}>
        <MDButton variant='text' color='info' size='small' iconOnly sx={{ marginLeft: 'auto' }}>
          {item.best_name}
        </MDButton>
      </Tooltip>
    );
  };

  const btnPrint = (item) => {
    return (
      <Tooltip title={t(`element.print`)}>
        <MDButton variant='text' color='info' size='small' iconOnly onClick={() => print(item)}>
          <Icon>print</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnBarcode = (item) => {
    if (item.barcode === '') return null;

    return (
      <Tooltip title='Barcode'>
        <MDButton
          variant='text'
          color='secondary'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
          onClick={() => print(item.barcode)}
        >
          <Icon>qr_code</Icon>
          {item.barcode}
        </MDButton>
      </Tooltip>
    );
  };

  const btnBarcode2 = (item) => {
    if (item.barcode2 === '')
      return (
        <MDButton
          variant='text'
          color='secondary'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
        >
          &nbsp;
        </MDButton>
      );

    return (
      <Tooltip title='Barcode 2'>
        <MDButton
          variant='text'
          color='secondary'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
          onClick={() => print(item.barcode2)}
        >
          <Icon>qr_code_2</Icon>
          {item.barcode2}
        </MDButton>
      </Tooltip>
    );
  };

  const btnMerge = (item) => {
    return (
      <MDButton variant='text' color='info' size='small' iconOnly onClick={() => merge(item)}>
        <Icon>merge</Icon> ผสาน
      </MDButton>
    );
  };

  const btnQrCode = (item) => {
    return (
      <Tooltip title='QR CODE'>
        <MDButton variant='text' color='error' size='small' iconOnly onClick={() => qr(item)}>
          <Icon>qr_code_scanner_outlined</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const btnPreview = (item) => {
    return (
      <Tooltip title='Preview'>
        <MDButton
          variant='text'
          color='info'
          size='small'
          iconOnly
          onClick={() => {
            window.open(item.url_info, '_blank');
          }}
        >
          <Icon>search</Icon>
        </MDButton>
      </Tooltip>
    );
  };

  const renderSearch = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={6} lg={6} xl={6}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view({ ...find, period: 'ALL', page: 1 });
              }}
            >
              <MDInput
                name='searchform'
                fullWidth
                value={find.search}
                type='search'
                onChange={(e) => setFind({ ...find, search: e.target.value, page: 1 })}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.search')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={6} md={6} lg={3}>
            <MDInput
              fullWidth
              value={find.grade}
              defaultValue='ALL'
              select
              onChange={(e) => view({ ...find, grade: e.target.value, page: 1 })}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    <span>Rating :</span>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value='ALL'>ทั้งหมด</MenuItem>
              {cboGrade.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.name}
                </MenuItem>
              ))}
            </MDInput>
          </Grid>
          <Grid item xs={12} sm={3} md={3} lg={1}>
            <MDBox display='flex' justifyContent='end' gap={1}>
              <MDButton variant='gradient' color='success' onClick={excel} disabled={excelLoading}>
                {excelLoading ? (
                  <>
                    <CircularProgress size={16} color='inherit' sx={{ mr: 1 }} />
                    Loading...
                  </>
                ) : (
                  <>
                    <Icon>table_view</Icon>&nbsp;Excel
                  </>
                )}
              </MDButton>
            </MDBox>
          </Grid>
          <Grid item xs={12} sm={3} md={3} lg={2}>
            <MDBox
              noValidate
              component='form'
              role='form'
              onSubmit={(e) => {
                e.preventDefault();
                view(find);
              }}
            >
              <MDInput
                name='searchpage'
                fullWidth
                value={find.page}
                type='number'
                onChange={(e) => setFind({ ...find, page: e.target.value })}
                onFocus={(e) => e.target.select()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position='start'>{t('element.page')} :</InputAdornment>
                  ),
                  autoComplete: 'off',
                }}
              />
            </MDBox>
          </Grid>
        </Grid>
      </Card>
    );
  };

  const renderAlert = () => {
    if (!alertProduct) return null;

    return (
      <MDBox mb={2}>
        <Alert severity='error' action={<ProductAlert />}>
          ตรวจพบข้อผิดพลาดของรหัส Barcode
        </Alert>
      </MDBox>
    );
  };

  const btnCostFix = (row) => {
    return (
      <Tooltip title='ราคาทุน'>
        <MDButton
          variant='text'
          color='warning'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
        >
          {Func.numberDisplay(row.cost_fix, 2)} ฿
        </MDButton>
      </Tooltip>
    );
  };

  const btnCost = (row) => {
    return (
      <Tooltip title='ราคาซื้อ'>
        <MDButton
          variant='text'
          color='error'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
        >
          {Func.numberDisplay(row.cost, 2)} ฿
        </MDButton>
      </Tooltip>
    );
  };

  const btnPrice = (row) => {
    return (
      <Tooltip title='ราคาขาย'>
        <MDButton
          variant='text'
          color='success'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
        >
          {Func.numberDisplay(row.price, 2)} ฿
        </MDButton>
      </Tooltip>
    );
  };

  const btnAffiliate = (row) => {
    return (
      <Tooltip title='ค่าหยิบสินค้า'>
        <MDButton
          variant='text'
          color='secondary'
          size='small'
          sx={{
            p: 0,
            m: 0,
            fontSize: '1rem',
            display: 'block',
            textAlign: 'right',
            minHeight: 'unset',
            fontWeight: 'normal',
          }}
        >
          {Func.numberDisplay(row.affiliate, 2)} ฿
        </MDButton>
      </Tooltip>
    );
  };

  const renderListItem = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={9} md={10} lg={11}>
          {renderSearch()}
        </Grid>
        <Grid item xs={12} sm={3} md={2} lg={1}>
          <MDButton
            variant='contained'
            color='dark'
            size='large'
            fullWidth
            sx={{ pt: 1.6, pb: 1.6 }}
            onClick={() => {
              setTextInfo([]);
              setTextError([]);
              setAttr(initAttr);
              setBrand({ id: '', name: '' });
              setUnit({ id: '', name: '' });
              setAddText(false);
              setConfirm(false);
              setModalAdd(true);
            }}
            disabled={modalView}
          >
            <Icon>post_add</Icon>
          </MDButton>
        </Grid>
        <Grid item xs={12} sx={{ minHeight: 500 }}>
          {loadView ? (
            <Grid container spacing={1}>
              {Array.from(Array(find.perPage)).map((_, index) => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={index.toString()}>
                  <Card>
                    <CardHeader
                      avatar={
                        <Skeleton
                          animation='wave'
                          width={40}
                          height={40}
                          sx={{ transform: 'scale(1, 1)' }}
                        />
                      }
                      title={<Skeleton animation='wave' height={10} width='40%' sx={{ mb: 1 }} />}
                      subheader={<Skeleton animation='wave' height={10} width='80%' />}
                    />
                    <CardContent sx={{ py: 0, px: 1.5 }}>
                      <Skeleton variant='rectangular' height={115} />
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={1}>
              {record.map((row) => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl={3} key={row.id}>
                  <Card className={row.enable === 'Y' ? 'bg-light' : 'bg-secondary'}>
                    <CardHeader
                      avatar={btnImage(row)}
                      title={btnOpen(row)}
                      subheader={
                        <MDBox className='cut' sx={{ overflow: 'hidden', width: 180 }}>
                          <Tooltip title={row.name}>
                            <MDTypography variant='caption'>{row.name}</MDTypography>
                          </Tooltip>
                        </MDBox>
                      }
                    />
                    <CardContent sx={{ py: 0, px: 1.5 }}>
                      <Grid container>
                        <Grid item xs={6}>
                          <MDTypography variant='body2'>{fgDisplay(row)}</MDTypography>
                          {btnBarcode(row)}
                          {btnBarcode2(row)}
                          {btnQuantity(row)}
                        </Grid>
                        {editPrice ? (
                          <Grid item xs={6} align='right'>
                            <ProductCost parent={row} reload={reload} />
                            <ProductSupplier parent={row} reload={reload} />
                            <ProductBranch parent={row} reload={reload} />
                            {btnAffiliate(row)}
                          </Grid>
                        ) : (
                          <Grid item xs={6} align='right'>
                            {btnCostFix(row)}
                            {btnCost(row)}
                            {btnPrice(row)}
                            {btnAffiliate(row)}
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                    <CardActions disableSpacing>
                      {btnQrCode(row)}
                      {btnPreview(row)}
                      {btnDel(row)}
                      {btnGrade(row)}
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <Grid container>
              <Grid item xs={12} display='flex' justifyContent='flex-start' gap={1}>
                <MDBox pl={2} sx={{ border: '1px solid #666666', borderRadius: '10px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={editPrice}
                        onChange={() => setEditPrice(!editPrice)}
                        color='success'
                        name='editPrice'
                      />
                    }
                    label='แก้ไขราคา'
                  />
                </MDBox>
                <MDBox pl={2} sx={{ border: '1px solid #666666', borderRadius: '10px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={find.nopic === 'Y'}
                        onChange={(e) =>
                          view({ ...find, nopic: e.target.checked ? 'Y' : 'N', page: 1 })
                        }
                        color='primary'
                        name='nopic'
                      />
                    }
                    label='ไม่มีรูปภาพ'
                  />
                </MDBox>
                <MDBox pl={2} sx={{ border: '1px solid #666666', borderRadius: '10px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={find.noprice === 'Y'}
                        onChange={(e) =>
                          view({ ...find, noprice: e.target.checked ? 'Y' : 'N', page: 1 })
                        }
                        color='info'
                        name='noprice'
                      />
                    }
                    label='ไม่มีราคาขาย'
                  />
                </MDBox>
                <MDBox pl={2} sx={{ border: '1px solid #666666', borderRadius: '10px' }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={find.noactive === 'Y'}
                        onChange={(e) =>
                          view({ ...find, noactive: e.target.checked ? 'Y' : 'N', page: 1 })
                        }
                        color='error'
                        name='noactive'
                      />
                    }
                    label='ยกเลิก'
                  />
                </MDBox>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item xs={12}>
          <Card sx={{ p: 2 }}>
            <MDBox display='flex' justifyContent='space-between' alignItems='flex-start'>
              <MDButton variant={show ? 'contained' : 'outlined'} color='dark' onClick={openShow}>
                {t(`element.show_record`, { num: find.perPage })}&nbsp;
                <Icon>keyboard_arrow_down</Icon>
              </MDButton>
              {renderShow}
              <Pagination
                count={pageTotal}
                variant='outlined'
                color='secondary'
                showFirstButton
                showLastButton
                page={pageNo}
                onChange={(e, p) => view({ ...find, page: p })}
              />
            </MDBox>
          </Card>
        </Grid>
      </Grid>
    );
  };

  const renderList = () => {
    return (
      <MDBox mb={7}>
        <Navbar route={[t(`menu.product`), t(`menu.product`)]} />
        {renderAlert()}
        {renderListItem()}
      </MDBox>
    );
  };

  const renderProductInfo = () => {
    return id === undefined ? (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          สินค้า
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='รหัสสินค้า *'
              onChange={(e) => setAttr({ ...attr, sku: e.target.value })}
              value={attr.sku}
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDBox
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, auto)',
                        gap: 0.5,
                        marginTop: -5,
                      }}
                    >
                      <MDButton
                        variant='text'
                        tabIndex={-1}
                        color='primary'
                        size='small'
                        onClick={() => checkCode('RT')}
                      >
                        RT
                      </MDButton>

                      <MDButton
                        variant='text'
                        tabIndex={-1}
                        color='info'
                        size='small'
                        onClick={() => checkCode('SV')}
                      >
                        SV
                      </MDButton>

                      <MDButton
                        variant='text'
                        tabIndex={-1}
                        color='success'
                        size='small'
                        onClick={() => checkCode('TS')}
                      >
                        TS
                      </MDButton>

                      <MDButton
                        variant='text'
                        tabIndex={-1}
                        color='warning'
                        size='small'
                        onClick={() => checkCode('PM')}
                      >
                        PM
                      </MDButton>
                    </MDBox>
                  </InputAdornment>
                ),
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='Barcode'
              onChange={(e) => setAttr({ ...attr, barcode: e.target.value })}
              value={attr.barcode}
              variant='standard'
              fullWidth
              inputProps={{
                maxLength: 20,
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='Barcode 2'
              onChange={(e) => setAttr({ ...attr, barcode2: e.target.value })}
              value={attr.barcode2}
              variant='standard'
              fullWidth
              inputProps={{
                maxLength: 20,
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    ) : (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          สินค้า
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {attr.image === '' ? null : <ProductImage parent={attr} callback={pin} size='large' />}
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รหัสสินค้า'
              value={attr.sku}
              variant='standard'
              fullWidth
              inputProps={{
                readOnly: true,
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='วันที่สร้าง'
              value={attr.created_at}
              variant='standard'
              fullWidth
              inputProps={{
                readOnly: true,
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPriceInfo = () => {
    return (
      <MDBox className='card' p={2}>
        <MDTypography variant='h6' mb={3}>
          ราคา (Standard)
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ราคาทุน'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, cost_fix: e.target.value })}
              value={attr.cost_fix}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>บาท</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ราคาซื้อ'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, cost: e.target.value })}
              value={attr.cost}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>บาท</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ราคาขาย'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, price: e.target.value })}
              value={attr.price}
              variant='standard'
              type='number'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>บาท</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderUnitInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          หน่วยนับ
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ปริมาณบรรจุ *'
              onChange={handlers.onChange('volume')}
              value={attr.volume}
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelectAuto
              label='หน่วยนับ *'
              data={cboUnit}
              value={unit || null}
              readOnly={attr.change_unit === 'N'}
              onChange={(e, v) => {
                e.preventDefault();
                setAttr({ ...attr, unit_id: v.id });
                return setUnit(v);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='หน่วยเติม'
              labelDefault={t(`element.please_select`)}
              data={cboUnit}
              value={attr.unitadd_id}
              onChange={(e) => setAttr({ ...attr, unitadd_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
                readOnly: attr.change_unit === 'N',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='หน่วยซื้อ'
              labelDefault={t(`element.please_select`)}
              data={cboUnit}
              value={attr.unitbuy_id}
              onChange={(e) => setAttr({ ...attr, unitbuy_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
                readOnly: attr.change_unit === 'N',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPharmacyInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          สินค้าประเภทยา
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDCheckbox
              label='ยา'
              checked={attr.pharmacy === 'Y'}
              onChange={(e) => setAttr({ ...attr, pharmacy: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          {attr.pharmacy === 'N' ? null : (
            <Grid item xs={12}>
              <MDCheckbox
                label='ยาสามัญประจำบ้าน'
                checked={attr.medicine === 'Y'}
                onChange={(e) => setAttr({ ...attr, medicine: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
          )}
          {attr.pharmacy === 'N' ? null : (
            <Grid item xs={12}>
              <MDCheckbox
                label='ข.ย. 9'
                checked={attr.drug_type_9 === 'Y'}
                onChange={(e) => setAttr({ ...attr, drug_type_9: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
          )}
          {attr.pharmacy === 'N' ? null : (
            <Grid item xs={12}>
              <MDCheckbox
                label='ข.ย. 10'
                checked={attr.drug_type_10 === 'Y'}
                onChange={(e) => setAttr({ ...attr, drug_type_10: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
          )}
          {attr.pharmacy === 'N' ? null : (
            <Grid item xs={12}>
              <MDCheckbox
                label='ข.ย. 11'
                checked={attr.drug_type_11 === 'Y'}
                onChange={(e) => setAttr({ ...attr, drug_type_11: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
          )}
        </Grid>
      </MDBox>
    );
  };

  const renderEtcInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          สถานะ
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDCheckbox
              label='สินค้าสำเร็จรูป'
              checked={attr.fg === 'Y'}
              onChange={(e) => setAttr({ ...attr, fg: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='วัตถุดิบ'
              checked={attr.rm === 'Y'}
              onChange={(e) => setAttr({ ...attr, rm: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='สถานะ เปิด/ปิด'
              checked={attr.enable === 'Y'}
              onChange={(e) => setAttr({ ...attr, enable: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='สามารถซื้อได้'
              checked={attr.active_buy === 'Y'}
              onChange={(e) => setAttr({ ...attr, active_buy: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          <Grid item xs={12}>
            <MDCheckbox
              label='ขายออนไลน์'
              checked={attr.sell_online === 'Y'}
              onChange={(e) => setAttr({ ...attr, sell_online: e.target.checked ? 'Y' : 'N' })}
            />
          </Grid>
          {id === undefined ? (
            <Grid item xs={12}>
              <MDCheckbox
                label='ตรวจสอบปริมาณ'
                checked={attr.check_stock === 'Y'}
                onChange={(e) => setAttr({ ...attr, check_stock: e.target.checked ? 'Y' : 'N' })}
              />
            </Grid>
          ) : null}
        </Grid>
      </MDBox>
    );
  };

  const renderStockInfo = () => {
    return attr.check_stock === 'Y' ? (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          ปริมาณแจ้งเตือน
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ปริมาณขั้นต่ำ'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, min_stock: e.target.value })}
              value={attr.min_stock}
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>{attr.unit_name}</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ปริมาณสูงสุด'
              onFocus={(e) => e.target.select()}
              onChange={(e) => setAttr({ ...attr, max_stock: e.target.value })}
              value={attr.max_stock}
              variant='standard'
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>
                    <MDTypography variant='body'>{attr.unit_name}</MDTypography>
                  </InputAdornment>
                ),
                inputProps: {
                  className: 'text-right',
                  autoComplete: 'off',
                },
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    ) : null;
  };

  const renderTypeInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          ประเภท
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={10}>
            <MDSelectAuto
              label='ยี่ห้อ'
              data={cboBrand}
              value={attr.id || attr.brand_id ? brand : null}
              onChange={(e, v) => {
                e.preventDefault();
                setAttr({ ...attr, brand_id: v.id });
                return setBrand(v);
              }}
            />
          </Grid>
          <Grid item xs={2} align='right'>
            <MDBox pt={2}>
              <ProductBrand callback={refresh} />
            </MDBox>
          </Grid>
          <Grid item xs={12}>
            <MDSelectGroup
              label='Department *'
              data={cboDep}
              value={attr.id || attr.dep_id ? dep : null}
              onChange={(e, v) => {
                e.preventDefault();
                setAttr({ ...attr, dep_id: v.id });
                return filterCategory(v);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelectGroup
              label='Category *'
              data={filterCat}
              value={attr.id || attr.cat_id ? cat : null}
              onChange={(e, v) => {
                e.preventDefault();
                setAttr({ ...attr, cat_id: v.id });
                return filterGroupFunc(v);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelectGroup
              label='Group *'
              data={filterGroup}
              value={attr.id || attr.group_id ? group : null}
              onChange={(e, v) => {
                e.preventDefault();
                setAttr({ ...attr, group_id: v.id });
                return filterSubGroupFunc(v);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelectGroup
              label='Sub Group *'
              data={filterSubGroup}
              value={attr.id || attr.subgroup_id ? subgroup : null}
              onChange={(e, v) => {
                e.preventDefault();
                setAttr({ ...attr, subgroup_id: v.id });
                return setSubGroup(v);
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDSelect
              label='ภาษีมูลค่าเพิ่ม *'
              labelDefault={t(`element.please_select`)}
              data={cboVatType}
              value={attr.vat_id}
              onChange={(e) => setAttr({ ...attr, vat_id: e.target.value })}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderThaiInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          ข้อมูลสินค้า (ภาษาไทย)
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อสินค้า *'
              onChange={(e) => setAttr({ ...attr, name: e.target.value })}
              value={attr.name}
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
              // onBeforeInput={(e) => {
              //   const value = [...e.target.value];
              //   const newChar = e.data;

              //   if (newChar && !newChar.match(/\p{M}/u) && isOverLimit(value)) {
              //     e.preventDefault();
              //   }
              // }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รายละเอียด ย่อ'
              onChange={(e) => setAttr({ ...attr, detail: e.target.value })}
              value={attr.detail}
              variant='standard'
              fullWidth
              multiline
              rows={3}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderEnglishInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          ข้อมูลสินค้า (ภาษาอังกฤษ)
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อสินค้า'
              onChange={(e) => setAttr({ ...attr, name_en: e.target.value })}
              value={attr.name_en}
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
              // onBeforeInput={(e) => {
              //   const value = [...e.target.value];
              //   const newChar = e.data;

              //   if (newChar && !newChar.match(/\p{M}/u) && isOverLimit(value)) {
              //     e.preventDefault();
              //   }
              // }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='รายละเอียด ย่อ'
              onChange={(e) => setAttr({ ...attr, detail_en: e.target.value })}
              value={attr.detail_en}
              variant='standard'
              fullWidth
              multiline
              rows={3}
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderDisplayInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          ชื่อสินค้าที่แสดง
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='ชื่อสินค้า'
              onChange={(e) => setAttr({ ...attr, display_name: e.target.value })}
              value={attr.display_name}
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
              onBeforeInput={(e) => {
                const value = [...e.target.value];
                const newChar = e.data;

                if (newChar && !newChar.match(/\p{M}/u) && isOverLimit(value)) {
                  e.preventDefault();
                }
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderDetailInfo = () => {
    return (
      <MDBox className='card' p={2} mb={2}>
        <MDTypography variant='h6' mb={3}>
          รายละเอียด
        </MDTypography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <MDInput
              label='เลขจดแจ้ง'
              onChange={(e) => setAttr({ ...attr, notification_no: e.target.value })}
              value={attr.notification_no}
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='อายุสินค้า (วัน)'
              onChange={handlers.onChange('shelf_life')}
              value={attr.shelf_life}
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ประเทศผู้ผลิต'
              onChange={(e) => setAttr({ ...attr, manufacturer: e.target.value })}
              value={attr.manufacturer}
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='กว้าง (มม.)'
              onChange={(e) => setAttr({ ...attr, width: e.target.value })}
              value={attr.width}
              type='number'
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='สูง (มม.)'
              onChange={(e) => setAttr({ ...attr, height: e.target.value })}
              value={attr.height}
              type='number'
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <MDInput
              label='ลึก (มม.)'
              onChange={(e) => setAttr({ ...attr, depth: e.target.value })}
              value={attr.depth}
              type='number'
              variant='standard'
              fullWidth
              multiline
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='น้ำหนักรวม (กรัม)'
              onChange={(e) => setAttr({ ...attr, gross_weight: e.target.value })}
              value={Number(attr.gross_weight)}
              type='number'
              variant='standard'
              fullWidth
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label='น้ำหนักสุทธิ (กรัม)'
              onChange={(e) => setAttr({ ...attr, net_weight: e.target.value })}
              value={Number(attr.net_weight)}
              type='number'
              variant='standard'
              fullWidth
              inputProps={{
                autoComplete: 'off',
              }}
            />
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderProductText = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} md={12} xl={4}>
          <MDBox className='card' p={2} mb={2}>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <MDTypography variant='h6'>ข้อมูลสินค้า</MDTypography>
              </Grid>
              <Grid item xs={6} align='right'>
                <MDButton
                  variant='text'
                  color='dark'
                  size='small'
                  onClick={() => {
                    window.open(excelExample, '_self');
                  }}
                >
                  <Icon>download</Icon> Download ตัวอย่าง
                </MDButton>
              </Grid>
              <Grid item xs={12}>
                <MDInput
                  label='วางข้อมูลจาก Excel / Google sheet'
                  onChange={(e) => setAttr({ ...attr, product_text: e.target.value })}
                  value={attr.product_text}
                  variant='standard'
                  fullWidth
                  multiline
                  rows={20}
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
            </Grid>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <MDBox className='card' p={2} mb={2}>
            <MDTypography variant='h6' mb={3} color='success'>
              รายการบันทึกสำเร็จ
            </MDTypography>
            <MDBox>
              <TableContainer sx={{ boxShadow: 0, minHeight: 500, maxHeight: 500 }}>
                <Table aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>รหัส</TableCell>
                      <TableCell>ชื่อสินค้า</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {textInfo.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align='left'>{row.code}</TableCell>
                        <TableCell align='left'>{row.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </MDBox>
        </Grid>
        <Grid item xs={12} md={6} xl={4}>
          <MDBox className='card' p={2} mb={2}>
            <MDTypography variant='h6' mb={3} color='error'>
              รายการบันทึกผิดพลาด
            </MDTypography>
            <MDBox>
              <TableContainer sx={{ boxShadow: 0, minHeight: 500, maxHeight: 500 }}>
                <Table aria-label='simple table'>
                  <TableHead>
                    <TableRow>
                      <TableCell>รหัส</TableCell>
                      <TableCell>ชื่อสินค้า</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {textError.map((row) => (
                      <TableRow key={row.id}>
                        <TableCell align='left'>{row.code}</TableCell>
                        <TableCell align='left'>{row.name}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </MDBox>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const renderAddInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} xl={3}>
          {renderProductInfo()}
          {renderPriceInfo()}
          {renderDetailInfo()}
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          {renderThaiInfo()}
          {renderEnglishInfo()}
          {renderDisplayInfo()}
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          {renderTypeInfo()}
          {renderUnitInfo()}
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          {renderEtcInfo()}
          {renderStockInfo()}
        </Grid>
      </Grid>
    );
  };

  const renderEditInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} xl={3}>
          {renderProductInfo()}
          {renderDetailInfo()}
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          {renderThaiInfo()}
          {renderEnglishInfo()}
          {renderDisplayInfo()}
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          {renderTypeInfo()}
          {renderUnitInfo()}
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          {renderPharmacyInfo()}
          {renderEtcInfo()}
        </Grid>
      </Grid>
    );
  };

  const renderAdd = () => {
    return (
      <Dialog open={modalAdd} maxWidth='xxl' keepMounted aria-describedby='dialog-add' fullWidth>
        <DialogTitle>
          {t(`element.add`)}
          <IconButton
            aria-label='close'
            onClick={() => setModalAdd(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox>{addText ? renderProductText() : renderAddInfo()}</MDBox>
        </DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant='text' color='error' onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='outlined' color='success' onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <Grid container>
              <Grid item xs={6}>
                {addText ? (
                  <MDButton
                    variant='text'
                    color='primary'
                    onClick={() => {
                      setTextInfo([]);
                      setTextError([]);
                      setAddText(false);
                    }}
                  >
                    <Icon>mode_edit_outline</Icon>&nbsp;สร้างสินค้าปกติ
                  </MDButton>
                ) : (
                  <MDButton
                    variant='text'
                    color='warning'
                    onClick={() => {
                      setTextInfo([]);
                      setTextError([]);
                      setAddText(true);
                    }}
                  >
                    <Icon>content_copy</Icon>&nbsp;สร้างด้วยข้อความ
                  </MDButton>
                )}
              </Grid>
              <Grid item xs={6} display='flex' justifyContent='flex-end'>
                <MDButton variant='text' color='info' onClick={addConfirm}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </Grid>
            </Grid>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderImage = () => {
    return (
      <Grid container spacing={2} sx={{ minHeight: 500 }}>
        <Grid item xs={12}>
          <MDBox mt={2} display='flex' justifyContent='end'>
            <ProductUpload uploadData={upload} />
          </MDBox>
        </Grid>
        <Grid item xs={12}>
          <MDBox>
            <Grid container spacing={2}>
              {image.map((item) => (
                <Grid key={item.id} item xs={12} md={4} xl={3}>
                  <Paper sx={{ p: 2 }} variant='outlined'>
                    <MDBox
                      component='img'
                      src={item.image}
                      alt={attr.name}
                      borderRadius='lg'
                      shadow='sm'
                      width='100%'
                      height='100%'
                      position='relative'
                      zIndex={10}
                    />
                    <MDBox align='right'>
                      <IconButton color='error' onClick={() => delImage(item)}>
                        <Icon>delete</Icon>
                      </IconButton>
                    </MDBox>
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const renderNavigate = () => {
    return (
      <Card sx={{ p: 2 }}>
        <Grid container>
          <Grid item xs={4}>
            <NavLink to={`/${mod}`}>
              <MDButton variant='contained' color='dark'>
                <Icon>arrow_back</Icon>&nbsp;{t(`element.back`)}
              </MDButton>
            </NavLink>
          </Grid>
          <Grid item xs={4}>
            <MDBox display='flex' justifyContent='center' gap={1}>
              <MDButton
                variant='contained'
                color='dark'
                onClick={() => {
                  view(find);
                  setModalView(true);
                }}
              >
                <Icon>search</Icon>
              </MDButton>
            </MDBox>
          </Grid>
          {navigate.first === '' ? null : (
            <Grid item xs={4}>
              <MDBox display='flex' justifyContent='flex-end' gap={1}>
                <NavLink to={`/${mod}/${navigate.first}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.first === id}
                  >
                    <Icon>first_page</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.prev}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.prev === id}
                  >
                    <Icon>navigate_before</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.next}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.next === id}
                  >
                    <Icon>navigate_next</Icon>
                  </MDButton>
                </NavLink>
                <NavLink to={`/${mod}/${navigate.last}`}>
                  <MDButton
                    variant='contained'
                    color='dark'
                    iconOnly
                    disabled={navigate.last === id}
                  >
                    <Icon>last_page</Icon>
                  </MDButton>
                </NavLink>
              </MDBox>
            </Grid>
          )}
        </Grid>
      </Card>
    );
  };

  const renderYoutube = () => {
    return (
      <Grid container spacing={2} sx={{ minHeight: 500 }}>
        <Grid item xs={12} md={12} lg={12} xl={8}>
          <MDBox className='card' p={2}>
            <Grid container spacing={2}>
              <Grid item xs={10}>
                <MDInput
                  label='Youtube ID'
                  onChange={(e) => setAttr({ ...attr, youtube: e.target.value })}
                  value={attr.youtube}
                  variant='standard'
                  fullWidth
                  inputProps={{
                    autoComplete: 'off',
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <MDButton variant='outlined' color='success' onClick={editYoutube}>
                  <Icon>save</Icon>&nbsp;{t(`element.save`)}
                </MDButton>
              </Grid>
              <Grid item xs={12}>
                {attr.youtube === '' ? null : (
                  <YouTube
                    videoId={attr.youtube}
                    opts={{
                      height: '360',
                      width: '100%',
                      playerVars: {
                        autoplay: 0,
                      },
                    }}
                    onReady={(e) => {
                      e.target.pauseVideo();
                    }}
                  />
                )}
              </Grid>
            </Grid>
          </MDBox>
        </Grid>
      </Grid>
    );
  };

  const renderDetail = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12} xl={6}>
          <MDBox className='card' p={2}>
            <MDTypography variant='h6' mb={3}>
              รายละเอียด ภาษาไทย
            </MDTypography>
            <MDEditor value={content} onChange={setContent} />
          </MDBox>
        </Grid>
        <Grid item xs={12} xl={6}>
          <MDBox className='card' p={2}>
            <MDTypography variant='h6' mb={3}>
              รายละเอียด ภาษาอังกฤษ
            </MDTypography>
            <MDEditor value={contentEN} onChange={setContentEN} />
          </MDBox>
        </Grid>
        <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
          <MDButton variant='outlined' color='success' onClick={editContent}>
            <Icon>save</Icon>&nbsp;{t(`element.save`)}
          </MDButton>
        </Grid>
      </Grid>
    );
  };

  const renderOpen = () => {
    return (
      <MDBox mb={7}>
        <Navbar route={[t(`menu.product`), `${attr.sku} : ${attr.name}`]} />
        {renderAlert()}
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {renderNavigate()}
          </Grid>
          <Grid item xs={12} sm={12} md={12}>
            <Card sx={{ p: 2 }}>
              <MDBox sx={{ width: '100%' }}>
                <MDBox>
                  <Tabs value={tab} onChange={(e, v) => setTab(v)} aria-label='tabs-addedit'>
                    <Tab label='SKU' />
                    <Tab label={t(`element.info`)} />
                    <Tab label='รายละเอียด' />
                    <Tab label='วีดีโอ' />
                    <Tab label='รูปภาพ' />
                    <Tab label='กลุ่มสินค้า' />
                  </Tabs>
                </MDBox>
                <TabPanel value={tab} index={0}>
                  <ProductBarcode />
                </TabPanel>
                <TabPanel value={tab} index={1}>
                  <Grid container>
                    <Grid item xs={12}>
                      {renderEditInfo()}
                      {confirm ? (
                        <Grid container mt={3}>
                          <Grid item xs={6}>
                            <MDButton
                              variant='outlined'
                              color='error'
                              onClick={() => setConfirm(false)}
                            >
                              <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                            </MDButton>
                          </Grid>
                          <Grid item xs={6} display='flex' justifyContent='flex-end'>
                            <MDButton variant='gradient' color='success' onClick={edit}>
                              <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                            </MDButton>
                          </Grid>
                        </Grid>
                      ) : (
                        <Grid container mt={3}>
                          <Grid item xs={12} display='flex' justifyContent='flex-end' gap={1}>
                            <MDButton variant='outlined' color='success' onClick={edit}>
                              <Icon>save</Icon>&nbsp;{t(`element.save`)}
                            </MDButton>
                          </Grid>
                        </Grid>
                      )}
                    </Grid>
                  </Grid>
                </TabPanel>
                <TabPanel value={tab} index={2}>
                  {renderDetail()}
                </TabPanel>
                <TabPanel value={tab} index={3}>
                  {renderYoutube()}
                </TabPanel>
                <TabPanel value={tab} index={4}>
                  {renderImage()}
                </TabPanel>
                <TabPanel value={tab} index={5}>
                  <ProductGroup />
                </TabPanel>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
    );
  };

  const renderPhoto = () => {
    return (
      <Dialog
        open={modalPhoto}
        maxWidth='sm'
        keepMounted
        aria-describedby='dialog-image'
        fullWidth
        onClose={() => setModalPhoto(false)}
      >
        <DialogTitle>
          {photo.name}
          <IconButton
            aria-label='close'
            onClick={() => setModalPhoto(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <MDBox
            component='img'
            src={photo.image}
            alt={photo.name}
            borderRadius='lg'
            shadow='sm'
            width='100%'
            position='relative'
            sx={{ boxShadow: 0 }}
          />
        </DialogContent>
      </Dialog>
    );
  };

  const renderView = () => {
    return (
      <Dialog
        open={modalView}
        maxWidth='xxl'
        keepMounted
        aria-describedby='dialog-view'
        fullWidth
        onClose={() => setModalView(false)}
      >
        <DialogTitle>
          ค้นหา
          <IconButton
            aria-label='close'
            onClick={() => setModalView(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>{renderListItem()}</DialogContent>
      </Dialog>
    );
  };

  const renderPrint = () => {
    return (
      <Dialog
        open={modalPrint}
        maxWidth='xs'
        keepMounted
        aria-describedby='dialog-print'
        fullWidth
        onClose={() => setModalPrint(false)}
      >
        <DialogTitle>
          {qrcode}
          <IconButton
            aria-label='close'
            onClick={() => setModalPrint(false)}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <Icon>close</Icon>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {printUrl === '' ? null : (
            <Iframe
              url={printUrl}
              width='100%'
              height='300'
              id='print'
              className=''
              display='initial'
              position='relative'
              frameBorder='0'
            />
          )}
        </DialogContent>
      </Dialog>
    );
  };

  const renderPage = () => {
    if (id === undefined) return renderList();
    return renderOpen();
  };

  useEffect(() => {
    i18n.changeLanguage(app.lang);

    init();
  }, [id]);

  return (
    <DashboardLayout>
      {renderPage()}
      {renderAdd()}
      {renderPhoto()}
      {renderView()}
      {renderPrint()}
      {renderLoading()}
      <Footer />
      {notify}
    </DashboardLayout>
  );
}

export default Product;
